import React, { useEffect, useMemo, useState } from 'react';
import { SearchableSelect } from '../Shared/SearchableSelect';
import { contracteService, lookupService } from '../../services/data';
import '../../styles/entityPages.css';

const emptyForm = {
  data: '',
  client: null,
  valoare: '',
  canal_vanzare: '',
  judet: '',
  confirmat: false,
  pj_pf: '',
};

const ContractePage = () => {
  const [items, setItems] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadData = async () => {
    setLoading(true);
    setError('');

    try {
      const [contractsData, lookupData] = await Promise.all([
        contracteService.list(),
        lookupService.getAll(),
      ]);
      setItems(contractsData);
      setClienti(lookupData.clienti || []);
    } catch (requestError) {
      setError(requestError.message || 'Nu am putut încărca contractele.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) {
      return items;
    }

    return items.filter((item) =>
      [item.client_name, item.judet, item.canal_vanzare, item.pj_pf, item.valoare]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term))
    );
  }, [items, search]);

  const clientOptions = useMemo(
    () => clienti.map((client) => ({ value: client.id, label: client.denumire || `Client ${client.id}` })),
    [clienti]
  );

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  const openCreateModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    try {
      if (editingId) {
        await contracteService.update(editingId, form);
      } else {
        await contracteService.create(form);
      }
      await loadData();
      closeModal();
    } catch (requestError) {
      setError(requestError.message || 'Nu am putut salva contractul.');
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setForm({
      data: item.data || '',
      client: item.client || null,
      valoare: item.valoare || '',
      canal_vanzare: item.canal_vanzare || '',
      judet: item.judet || '',
      confirmat: !!item.confirmat,
      pj_pf: item.pj_pf || '',
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sigur vrei să ștergi acest contract?')) {
      return;
    }

    setError('');
    try {
      await contracteService.remove(id);
      await loadData();
      if (editingId === id) {
        resetForm();
      }
    } catch (requestError) {
      setError(requestError.message || 'Nu am putut șterge contractul.');
    }
  };

  return (
    <div className="entity-page">
      <div className="entity-page-header">
        <p>Administrare contracte cu selecție client și acțiuni complete.</p>
      </div>

      {error && <div className="entity-error">{error}</div>}

      <div className="entity-page-toolbar">
        <button type="button" className="btn-primary" onClick={openCreateModal}>
          Adaugă contract
        </button>
        <input
          className="entity-search"
          type="text"
          placeholder="Caută contract..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      <div className="entity-table-wrap">
        {loading ? (
          <div className="entity-loading">Se încarcă...</div>
        ) : (
          <table className="entity-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Data</th>
                <th>Client</th>
                <th>Valoare</th>
                <th>Canal</th>
                <th>Județ</th>
                <th>PJ/PF</th>
                <th>Confirmat</th>
                <th>Acțiuni</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item) => (
                <tr key={item.id}>
                  <td data-label="#">{item.id}</td>
                  <td data-label="Data">{item.data || '-'}</td>
                  <td data-label="Client">{item.client_name || '-'}</td>
                  <td data-label="Valoare">{item.valoare || '-'}</td>
                  <td data-label="Canal">{item.canal_vanzare || '-'}</td>
                  <td data-label="Județ">{item.judet || '-'}</td>
                  <td data-label="PJ/PF">{item.pj_pf || '-'}</td>
                  <td data-label="Confirmat">{item.confirmat ? 'Da' : 'Nu'}</td>
                  <td>
                    <div className="table-actions">
                      <button type="button" className="btn-inline" onClick={() => handleEdit(item)}>
                        Edit
                      </button>
                      <button type="button" className="btn-inline danger" onClick={() => handleDelete(item.id)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!filteredItems.length && (
                <tr>
                  <td colSpan={9} className="entity-empty">
                    Nu există date.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {isModalOpen && (
        <div className="entity-modal-overlay" onClick={closeModal}>
          <div className="entity-modal" onClick={(event) => event.stopPropagation()}>
            <div className="entity-modal-header">
              <h3>{editingId ? 'Edit contract' : 'Adaugă contract'}</h3>
              <button type="button" className="entity-modal-close" onClick={closeModal}>×</button>
            </div>

            <form className="entity-form entity-form-in-modal" onSubmit={handleSubmit}>
              <div className="entity-form-grid">
                <div className="entity-field">
                  <label>Data</label>
                  <input
                    type="date"
                    value={form.data || ''}
                    onChange={(event) => setForm((prev) => ({ ...prev, data: event.target.value }))}
                  />
                </div>

                <div className="entity-field">
                  <label>Client</label>
                  <SearchableSelect
                    options={clientOptions}
                    value={form.client}
                    onChange={(value) => setForm((prev) => ({ ...prev, client: value }))}
                    placeholder="Caută client..."
                  />
                </div>

                <div className="entity-field">
                  <label>Valoare</label>
                  <input
                    type="number"
                    step="0.00001"
                    value={form.valoare || ''}
                    onChange={(event) => setForm((prev) => ({ ...prev, valoare: event.target.value }))}
                  />
                </div>

                <div className="entity-field">
                  <label>Canal vânzare</label>
                  <input
                    type="text"
                    value={form.canal_vanzare || ''}
                    onChange={(event) => setForm((prev) => ({ ...prev, canal_vanzare: event.target.value }))}
                  />
                </div>

                <div className="entity-field">
                  <label>Județ</label>
                  <input
                    type="text"
                    value={form.judet || ''}
                    onChange={(event) => setForm((prev) => ({ ...prev, judet: event.target.value }))}
                  />
                </div>

                <div className="entity-field">
                  <label>PJ/PF</label>
                  <input
                    type="text"
                    value={form.pj_pf || ''}
                    onChange={(event) => setForm((prev) => ({ ...prev, pj_pf: event.target.value }))}
                  />
                </div>

                <div className="entity-field entity-checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={!!form.confirmat}
                      onChange={(event) => setForm((prev) => ({ ...prev, confirmat: event.target.checked }))}
                    />
                    <span>Confirmat</span>
                  </label>
                </div>
              </div>

              <div className="entity-form-actions">
                <button type="submit" className="btn-primary">
                  {editingId ? 'Actualizează contract' : 'Adaugă contract'}
                </button>
                <button type="button" className="btn-secondary" onClick={closeModal}>
                  Anulează
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractePage;
